/**
 * Guides list page (public)
 *
 * Future-proof design with:
 * - Featured guides section
 * - Category grouping
 * - Sorting and filtering
 * - Enhanced card design
 * - Scalable for many guides
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HiSearch, HiSparkles, HiClock } from 'react-icons/hi';
import { usePageSeo } from '../utils/seo';
import { DEFAULT_OG_IMAGE, PAGE_SEO } from '../utils/seoDefaults';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';
import { publicLoadErrorMessage } from '../lib/publicSiteErrorMessage';

// Popular Products Section Component
function PopularProductsSection() {
  const [products, setProducts] = useState<Array<{ id: string; name: string; price: number; image_url: string | null }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const API_URL = import.meta.env.VITE_API_URL || '';
        const url = API_URL ? `${API_URL}/api/menu?popular=true` : '/api/menu?popular=true';
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          const items = (data.items || []).slice(0, 4);
          setProducts(items);
        }
      } catch (err) {
        console.error('Failed to fetch popular products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading || products.length === 0) return null;

  return (
    <section className="mt-12">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-display font-bold text-gray-900 mb-2">
          🔥 Popular Treats
        </h3>
        <p className="text-gray-600">Customer favorites you might love!</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {products.map((product) => (
          <Link
            key={product.id}
            to="/menu"
            className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg hover:border-purple-300 transition-all group"
          >
            {product.image_url ? (
              <div className="aspect-square rounded-lg overflow-hidden mb-3 bg-gray-100">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="aspect-square rounded-lg bg-gray-100 mb-3 flex items-center justify-center text-4xl">
                🧁
              </div>
            )}
            <h4 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-2">{product.name}</h4>
            <p className="text-primary-600 font-bold">${product.price.toFixed(2)}</p>
          </Link>
        ))}
      </div>
      <div className="text-center mt-6">
        <Link
          to="/menu"
          className="inline-flex items-center gap-2 text-purple-700 hover:text-purple-800 font-semibold"
        >
          View Full Menu →
        </Link>
      </div>
    </section>
  );
}

type Guide = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  keywords: string[] | null;
  city: string | null;
  published_at: string | null;
};

type SortOption = 'recent' | 'alphabetical' | 'popular';

// Category mapping from keywords
const CATEGORY_KEYWORDS: Record<string, string[]> = {
  'Ordering & Services': ['order', 'ordering', 'custom', 'cake', 'pickup', 'delivery', 'service', 'how to order'],
  'Baking Tips': ['baking', 'tip', 'recipe', 'storage', 'fresh', 'preserve', 'keep fresh'],
  'Local Information': ['barrie', 'local', 'location', 'area', 'near me', 'best bakery'],
  'Product Guides': ['product', 'item', 'menu', 'treat', 'dessert', 'pastry'],
};

// Detect category from keywords
const getCategory = (guide: Guide): string => {
  const keywords = (guide.keywords || []).map(k => k.toLowerCase());
  const titleLower = guide.title.toLowerCase();
  const excerptLower = (guide.excerpt || '').toLowerCase();
  const allText = `${titleLower} ${excerptLower} ${keywords.join(' ')}`;

  for (const [category, categoryKeywords] of Object.entries(CATEGORY_KEYWORDS)) {
    if (categoryKeywords.some(kw => allText.includes(kw))) {
      return category;
    }
  }
  return 'General';
};

// Check if guide should be featured (has "featured" keyword or is in top 3 most recent)
const isFeatured = (guide: Guide, index: number): boolean => {
  const keywords = (guide.keywords || []).map(k => k.toLowerCase());
  return keywords.includes('featured') || index < 3;
};

export default function Guides() {
  const { getImage } = useSiteImageOverrides();
  usePageSeo({
    ...PAGE_SEO.guides,
    og: {
      type: 'website',
      title: PAGE_SEO.guides.title,
      description: PAGE_SEO.guides.description,
      image: getImage('og_image', DEFAULT_OG_IMAGE),
    },
  });
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('recent');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/articles?limit=100');
        if (!res.ok) {
          console.error('Guides list fetch failed:', res.status);
          setError(publicLoadErrorMessage());
          return;
        }
        const data = await res.json();
        setGuides(data.articles || []);
      } catch (e) {
        console.error('Guides list load:', e);
        setError(publicLoadErrorMessage());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Process guides: filter, sort, categorize
  const processedGuides = useMemo(() => {
    let result = [...guides];

    // Search filter
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      result = result.filter((g) => {
        const hay = `${g.title} ${g.slug} ${g.excerpt || ''} ${(g.keywords || []).join(' ')}`.toLowerCase();
        return hay.includes(term);
      });
    }

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(g => getCategory(g) === selectedCategory);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'recent': {
          const dateA = a.published_at ? new Date(a.published_at).getTime() : 0;
          const dateB = b.published_at ? new Date(b.published_at).getTime() : 0;
          return dateB - dateA; // Newest first
        }
        case 'alphabetical':
          return a.title.localeCompare(b.title);
        case 'popular': {
          // For now, use recent as proxy for popular (can be enhanced with view counts later)
          const dateA2 = a.published_at ? new Date(a.published_at).getTime() : 0;
          const dateB2 = b.published_at ? new Date(b.published_at).getTime() : 0;
          return dateB2 - dateA2;
        }
        default:
          return 0;
      }
    });

    return result;
  }, [guides, search, sortBy, selectedCategory]);

  // Separate featured and regular guides
  const { featuredGuides, regularGuides } = useMemo(() => {
    const featured: Guide[] = [];
    const regular: Guide[] = [];

    processedGuides.forEach((guide, index) => {
      if (isFeatured(guide, index)) {
        featured.push(guide);
      } else {
        regular.push(guide);
      }
    });

    // Limit featured to 3
    return {
      featuredGuides: featured.slice(0, 3),
      regularGuides: regular,
    };
  }, [processedGuides]);

  // Group regular guides by category
  const guidesByCategory = useMemo(() => {
    const grouped: Record<string, Guide[]> = {};
    regularGuides.forEach(guide => {
      const category = getCategory(guide);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(guide);
    });
    return grouped;
  }, [regularGuides]);

  // Get all available categories
  const availableCategories = useMemo(() => {
    const cats = new Set<string>();
    processedGuides.forEach(g => cats.add(getCategory(g)));
    return Array.from(cats).sort();
  }, [processedGuides]);

  // Guide card component
  const GuideCard = ({ guide, featured = false }: { guide: Guide; featured?: boolean }) => {
    const category = getCategory(guide);
    const readTime = Math.max(2, Math.ceil((guide.excerpt?.length || 0) / 200)); // Rough estimate

    return (
      <Link
        to={`/guides/${guide.slug}`}
        className={`group bg-white rounded-2xl border-2 ${
          featured
            ? 'border-purple-300 shadow-lg hover:shadow-xl hover:border-purple-400'
            : 'border-gray-200 hover:border-purple-300 hover:shadow-md'
        } p-6 transition-all`}
      >
        {featured && (
          <div className="flex items-center gap-2 mb-3">
            <HiSparkles className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">
              Featured
            </span>
          </div>
        )}

        <div className="flex items-start justify-between gap-3 mb-2">
          <h2 className={`font-semibold text-gray-900 group-hover:text-purple-700 transition-colors ${
            featured ? 'text-xl' : 'text-lg'
          }`}>
            {guide.title}
          </h2>
          {guide.city && (
            <span className="text-xs bg-purple-50 text-purple-700 px-2.5 py-1 rounded-full whitespace-nowrap font-medium">
              {guide.city}
            </span>
          )}
        </div>

        {guide.excerpt && (
          <p className={`text-gray-600 mt-2 ${featured ? 'line-clamp-4' : 'line-clamp-3'}`}>
            {guide.excerpt}
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full font-medium">
            {category}
          </span>
          {guide.published_at && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <HiClock className="w-3 h-3" />
              {new Date(guide.published_at).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                year: 'numeric'
              })}
            </span>
          )}
          <span className="text-xs text-gray-500">
            {readTime} min read
          </span>
        </div>

        {(guide.keywords || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(guide.keywords || []).slice(0, 3).map((k) => (
              <span
                key={k}
                className="text-xs bg-gray-50 text-gray-600 px-2 py-0.5 rounded-full"
              >
                {k}
              </span>
            ))}
          </div>
        )}

        <div className={`mt-4 text-sm font-medium text-purple-700 group-hover:text-purple-800 transition-colors ${
          featured ? 'text-base' : ''
        }`}>
          Read guide →
        </div>
      </Link>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl sm:text-5xl font-display font-bold text-gray-900 mb-3">
          Helpful Guides
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Everything you need to know about ordering, baking tips, and more from Nanny B&apos;s Bakery.
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
            <HiSearch className="w-5 h-5 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search guides..."
              className="w-full bg-transparent outline-none text-gray-900 placeholder-gray-400"
            />
          </div>

          {/* Category Filter */}
          {availableCategories.length > 0 && (
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl bg-white text-gray-900 font-medium focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              aria-label="Filter by category"
            >
              <option value="all">All Categories</option>
              {availableCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="px-4 py-3 border border-gray-200 rounded-xl bg-white text-gray-900 font-medium focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            aria-label="Sort guides"
          >
            <option value="recent">Most Recent</option>
            <option value="alphabetical">Alphabetical</option>
            <option value="popular">Most Popular</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-600">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          Loading guides...
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="bg-red-50 rounded-2xl border border-red-200 p-6 text-red-700 text-center">
          {error}
        </div>
      )}

      {/* No Results */}
      {!loading && !error && processedGuides.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <p className="text-gray-600 text-lg mb-2">No guides found</p>
          <p className="text-gray-500 text-sm">
            {search ? 'Try a different search term' : 'Check back soon for helpful guides!'}
          </p>
        </div>
      )}

      {/* Content */}
      {!loading && !error && processedGuides.length > 0 && (
        <>
          {/* Featured Guides Section */}
          {featuredGuides.length > 0 && (
            <section className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <HiSparkles className="w-6 h-6 text-purple-600" />
                <h2 className="text-2xl font-display font-bold text-gray-900">Featured Guides</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredGuides.map((guide) => (
                  <GuideCard key={guide.id} guide={guide} featured />
                ))}
              </div>
            </section>
          )}

          {/* Category Sections */}
          {Object.keys(guidesByCategory).length > 0 && (
            <div className="space-y-12">
              {Object.entries(guidesByCategory)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, categoryGuides]) => (
                  <section key={category}>
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">
                      {category}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {categoryGuides.map((guide) => (
                        <GuideCard key={guide.id} guide={guide} />
                      ))}
                    </div>
                  </section>
                ))}
            </div>
          )}

          {/* Fallback: All Guides Grid (if no categories) */}
          {Object.keys(guidesByCategory).length === 0 && regularGuides.length > 0 && (
            <section>
              <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">All Guides</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {regularGuides.map((guide) => (
                  <GuideCard key={guide.id} guide={guide} />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* Results Count */}
      {!loading && !error && processedGuides.length > 0 && (
        <div className="mt-8 text-center text-gray-600">
          <p className="text-sm">
            Showing {processedGuides.length} guide{processedGuides.length !== 1 ? 's' : ''}
            {search && ` matching "${search}"`}
            {selectedCategory !== 'all' && ` in ${selectedCategory}`}
          </p>
        </div>
      )}

      {/* Conversion Section: Special Offer for Guide Readers */}
      {!loading && !error && processedGuides.length > 0 && (
        <section className="mt-12">
          <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-pink-50 rounded-2xl p-8 border-2 border-amber-200 shadow-lg">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-bold mb-4">
                <span>🎁</span>
                <span>SPECIAL OFFER FOR READERS</span>
              </div>
              <h3 className="text-3xl font-display font-bold text-gray-900 mb-2">
                Love Our Guides? Try Our Treats!
              </h3>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                Mention you found us through our guides and get <strong className="text-amber-700">10% off your first order</strong> or a <strong className="text-amber-700">free cookie with any $15+ purchase</strong>!
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/menu"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Browse Our Menu
              </Link>
              <Link
                to="/order"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-purple-700 rounded-xl font-bold text-lg border-2 border-purple-300 hover:bg-purple-50 transition-all shadow-md"
              >
                Order Now
              </Link>
              <a
                href="tel:7055034966"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold border-2 border-gray-300 hover:bg-gray-50 transition-all"
              >
                📞 Call (705) 503-4966
              </a>
            </div>
            <p className="text-center text-sm text-gray-600 mt-4">
              💡 <strong>Tip:</strong> When you call or order, mention "I read your guides!" to claim your discount
            </p>
          </div>
        </section>
      )}

      {/* Conversion Section: Popular Products */}
      {!loading && !error && processedGuides.length > 0 && (
        <PopularProductsSection />
      )}

      {/* Main CTA */}
      <div className="mt-16 bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl p-8 text-white shadow-xl">
        <h3 className="text-2xl font-display font-bold mb-2">Ready to order?</h3>
        <p className="text-purple-100 mb-6 max-w-2xl">
          Browse our menu or place an order online. We&apos;ll confirm pickup details with you.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/menu"
            className="inline-flex justify-center px-6 py-3 bg-white text-purple-700 rounded-xl font-semibold hover:bg-purple-50 transition-colors shadow-lg"
          >
            View Menu
          </Link>
          <Link
            to="/order"
            className="inline-flex justify-center px-6 py-3 bg-white/20 text-white rounded-xl font-semibold hover:bg-white/30 transition-colors border-2 border-white/30"
          >
            Place an Order
          </Link>
        </div>
      </div>
    </div>
  );
}


